import { useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { activatePlusMembership } from 'api/jalapeno';
import useAccountQuery, { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { PLUS_MEMBERSHIP_OPTIONS_QUERY_KEY } from 'api/queries/usePlusMembershipOptionsQuery';
import { BenefitsList } from 'features/paid-membership/BenefitsList';
import { MembershipDescriptions } from 'features/paid-membership/MembershipDescriptions';
import Button from 'honeydew/Button';
import { LoadingIcon } from 'honeydew/Loader';
import Modal from 'honeydew/Modal';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import useBrandLinks from 'hooks/useBrandLinks';
import useGlobalStore from 'hooks/useGlobalStore';
import { trackEvent } from 'utils/analyticsUtils';

import { MembershipStateContext } from '../MembershipStateContext';

export function JoinModal({ onClose: onCloseProp, open }) {
    const { isMisfitsTheme } = useGlobalStore();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const {
        availablePlusMembership,
        availableTrialMembership,
        plusMembershipBranding,
        price: { annual: annualPlusMembershipCost },
    } = useAccountPlusMembershipHook();
    const { data: account } = useAccountQuery();
    const { terms } = useBrandLinks();
    const membershipJoiningContext = useContext(MembershipStateContext);
    const { joinPlusMembership, joinPlusTrial } = membershipJoiningContext ?? {};
    const [didTrackAnalyticEvent, setDidTrackAnalyticEvent] = useState(false);
    const [isJoinApiCallInProgress, setIsJoinApiCallInProgress] = useState(false);

    const customerID = account?.customerID;
    const plusMembershipVariantID = availableTrialMembership?.variantId ?? availablePlusMembership?.variantId;
    const typeOfMembership = availableTrialMembership ? 'FREE_TRIAL' : 'PAID';
    const ctaButtonEnabled =
        !!customerID && !!plusMembershipVariantID && !!typeOfMembership && !isJoinApiCallInProgress;
    const ctaCopy = availableTrialMembership ? 'Start free trial today' : `Confirm ${plusMembershipBranding}`;
    const headingCopy = `Save up to 50% with ${plusMembershipBranding}`;
    const trialDurationDays = availableTrialMembership?.trialDurationDays;

    const onClose = () => {
        setDidTrackAnalyticEvent(false);
        onCloseProp();
    };

    useEffect(() => {
        if (didTrackAnalyticEvent) {
            return;
        }

        if (!open) {
            return;
        }

        if (!availableTrialMembership && !availablePlusMembership) {
            return;
        }

        setDidTrackAnalyticEvent(true);

        if (availableTrialMembership) {
            trackEvent('open join free trial modal', { path: window.location.pathname });
        }

        if (availablePlusMembership) {
            trackEvent('open membership start modal', { path: window.location.pathname });
        }
    }, [didTrackAnalyticEvent, availableTrialMembership, availablePlusMembership, open]);

    const activationMethod = async () => {
        const response = await activatePlusMembership({
            customerID,
            sendParams: { plusMembershipVariantID, typeOfMembership },
        });

        if (response?.msg === 'Success') {
            if (typeOfMembership === 'FREE_TRIAL') {
                trackEvent('start free trial', {
                    source: 'account',
                });
            } else {
                trackEvent('start membership', {
                    source: 'account',
                });
            }

            queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [PLUS_MEMBERSHIP_OPTIONS_QUERY_KEY] });

            if (typeOfMembership === 'PAID') {
                joinPlusMembership?.();
            } else {
                joinPlusTrial?.();
            }

            if (window.isNativeApp) {
                window.ReactNativeWebView?.postMessage(JSON.stringify({ reload: true }));
            }

            navigate('/account/membership');
        } else {
            setIsJoinApiCallInProgress(false);
        }
    };

    return (
        <Modal onClose={onClose} open={open} topGradientBorder>
            <div className="flex w-full flex-col items-center gap-20 md:px-35">
                <div className="flex flex-col items-center text-center">
                    <h1 className="text-heading-sm">{headingCopy}</h1>
                </div>
                <div className="text-body-sm rounded-md bg-off-white px-25 py-10">
                    <span className="font-grotesk-bold">Pays for itself</span> with just one order per month*
                </div>
                <div className="relative flex flex-col items-center gap-30 lg:flex-row lg:gap-[71px]">
                    <div className="flex-1">
                        <BenefitsList includeAsterisks />
                    </div>
                    <hr className="h-[1px] w-full border-t-[1px] border-grey-light lg:absolute lg:left-1/2 lg:h-full lg:w-[1px] lg:border-l-[1px] lg:border-t-0" />
                    <div className="text-body-sm flex flex-1 flex-col gap-10">
                        <MembershipDescriptions />
                    </div>
                </div>
                <div className="flex flex-col gap-10">
                    <Button
                        size="md"
                        onClick={() => {
                            if (isJoinApiCallInProgress) {
                                return;
                            }

                            setIsJoinApiCallInProgress(true);

                            activationMethod();
                        }}
                        variant="plusMembership"
                        disabled={!ctaButtonEnabled}
                    >
                        {isJoinApiCallInProgress ? (
                            <div className="mt-[3px] flex">
                                <LoadingIcon />
                            </div>
                        ) : (
                            ctaCopy
                        )}
                    </Button>
                    <p className="text-body-xs text-grey">
                        By clicking “{ctaCopy}”, you are agreeing to an annual {plusMembershipBranding} membership that
                        will automatically continue until you cancel. You authorize us to charge you $
                        {annualPlusMembershipCost}/year (plus applicable taxes){' '}
                        {availableTrialMembership
                            ? `after your ${trialDurationDays} day free trial ends`
                            : 'upon signup'}{' '}
                        and on each annual anniversary thereafter. Cancel at any time via your {plusMembershipBranding}{' '}
                        Membership page. For more info, see our&nbsp;
                        <a className="underline" href={terms} target="_blank" rel="noopener noreferrer">
                            Terms of Service
                        </a>
                        .
                    </p>
                    <div className="flex flex-col">
                        <p className="text-body-xs text-grey">
                            *Based on projected savings on the average{' '}
                            {isMisfitsTheme ? 'Misfits Market' : 'Imperfect Foods'} customer order. Actual savings may
                            vary by customer.
                        </p>
                        <p className="text-body-xs text-grey">
                            **We calculate savings by comparing the price of our products to that of other grocers and a
                            product’s MSRP if applicable. Actual savings may vary.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

JoinModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
};
